import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import DashboardSidebar from "./components/DashboardSidebar";
import StatsCards from "./components/StatsCards";
import RecentHistory from "./components/RecentHistory";
import SendChart from "./components/SendChart";
import QuickSend from "./components/QuickSend";
import ChargeModal from "./components/ChargeModal";
import UserNotificationBell from "./components/UserNotificationBell";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileSlideMenu from "./components/MobileSlideMenu";
import RecentDeletionLogs from "./components/RecentDeletionLogs";
import BalanceFailureBanner from "@/components/feature/BalanceFailureBanner";
import { useScheduledSendPoller } from "@/hooks/useScheduledSendPoller";
import { useBalanceFailureAlert } from "@/hooks/useBalanceFailureAlert";

interface DashboardNotice {
  id: number;
  badge: string;
  title: string;
  created_at: string;
  is_pinned: boolean;
}

interface Toast {
  id: string;
  type: "success" | "error";
  message: string;
  amount?: number;
}

interface DashboardStatsCache {
  credit: number;
  monthlySent: number;
  successRate: number;
  scheduledCount: number;
  weeklyStats: { day: string; sms: number; lms: number; mms: number }[];
  monthlyStats: { day: string; sms: number; lms: number; mms: number }[];
  scheduledItems: { id: string; title: string; target: string; time: string; type: string }[];
}

// 탭 복귀(리로드) 시 로딩 없이 마지막 데이터를 즉시 보여주기 위한 캐시
const STATS_CACHE_KEY = "cache:user_dashboard_stats";
const NOTICES_CACHE_KEY = "cache:user_dashboard_notices";

function readStatsCache(): DashboardStatsCache | null {
  try {
    const raw = sessionStorage.getItem(STATS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DashboardStatsCache;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeStatsCache(value: DashboardStatsCache): void {
  try {
    sessionStorage.setItem(STATS_CACHE_KEY, JSON.stringify(value));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

function readNoticesCache(): DashboardNotice[] | null {
  try {
    const raw = sessionStorage.getItem(NOTICES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DashboardNotice[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeNoticesCache(value: DashboardNotice[]): void {
  try {
    sessionStorage.setItem(NOTICES_CACHE_KEY, JSON.stringify(value));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, loading, session, perMessageRate } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // 캐시 복원: 저장된 마지막 데이터로 즉시 렌더 후 조용히 갱신
  const statsCacheRef = useRef<DashboardStatsCache | null>(readStatsCache());
  const noticesCacheRef = useRef<DashboardNotice[] | null>(readNoticesCache());

  const [statsLoading, setStatsLoading] = useState(statsCacheRef.current === null);
  const [credit, setCredit] = useState(statsCacheRef.current?.credit ?? 0);
  const [monthlySent, setMonthlySent] = useState(statsCacheRef.current?.monthlySent ?? 0);
  const [successRate, setSuccessRate] = useState(statsCacheRef.current?.successRate ?? 0);
  const [scheduledCount, setScheduledCount] = useState(statsCacheRef.current?.scheduledCount ?? 0);
  const [weeklyStats, setWeeklyStats] = useState<{ day: string; sms: number; lms: number; mms: number }[]>(statsCacheRef.current?.weeklyStats ?? []);
  const [monthlyStats, setMonthlyStats] = useState<{ day: string; sms: number; lms: number; mms: number }[]>(statsCacheRef.current?.monthlyStats ?? []);
  const [scheduledItems, setScheduledItems] = useState<{ id: string; title: string; target: string; time: string; type: string }[]>(statsCacheRef.current?.scheduledItems ?? []);
  const [scheduledLoading, setScheduledLoading] = useState(statsCacheRef.current === null);
  const [notices, setNotices] = useState<DashboardNotice[]>(noticesCacheRef.current ?? []);
  const [noticesLoading, setNoticesLoading] = useState(noticesCacheRef.current === null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // 최근 "잔액 부족" 실패 감지 (대시보드 경고 배너)
  const balanceAlert = useBalanceFailureAlert(user?.id);
  const refreshBalanceAlert = balanceAlert.refresh;

  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    // 캐시가 있으면 로딩 화면 없이 조용히 갱신
    if (statsCacheRef.current === null) setStatsLoading(true);

    try {
      const { data: profileData, error: profileError } = await supabase.functions.invoke("get-user-profile", {
        body: { user_id: user.id },
      });

      if (profileError) {
        console.warn("[Dashboard] get-user-profile 실패 (세션 만료 가능):", profileError);
      } else if (profileData?.error) {
        console.warn("[Dashboard] get-user-profile 오류:", profileData.error);
      } else if (profileData?.credit !== undefined && profileData?.credit !== null) {
        setCredit(Number(profileData.credit));
      }
    } catch (err) {
      console.warn("[Dashboard] 프로필 조회 예외:", err);
    }

    try {
      const { data: historyData, error: historyError } = await supabase.functions.invoke("get-user-send-stats", {
        body: { user_id: user.id },
      });

      if (historyError) {
        console.error("[Dashboard] get-user-send-stats 오류:", historyError);
      } else {
        const monthHistory = historyData?.monthHistory ?? [];
        const sent = monthHistory.filter((h: any) => h.status !== "scheduled");
        const totalSuccess = sent.reduce((s: number, h: any) => s + (h.success_count ?? 0), 0);
        const totalFail = sent.reduce((s: number, h: any) => s + (h.fail_count ?? 0), 0);
        const total = totalSuccess + totalFail;
        setMonthlySent(total);
        setSuccessRate(total > 0 ? Math.round((totalSuccess / total) * 100) : 0);
        setScheduledCount(monthHistory.filter((h: any) => h.status === "scheduled").length);
        setWeeklyStats(historyData?.weeklyStats ?? []);
        setMonthlyStats(historyData?.monthlyStats ?? []);
      }
    } catch (err) {
      console.error("[Dashboard] 발송 통계 조회 예외:", err);
    }

    try {
      if (statsCacheRef.current === null) setScheduledLoading(true);
      const { data: scheduledData } = await supabase
        .from("send_history")
        .select("*, send_recipients(name, phone)")
        .eq("user_id", user.id)
        .eq("status", "scheduled")
        .order("scheduled_at", { ascending: true })
        .limit(5);

      if (scheduledData) {
        const mapped = scheduledData.map((row: any) => {
          const recipients = row.send_recipients ?? [];
          const total = recipients.length || row.total_count || 0;
          const target = total === 1 && recipients[0]
            ? (recipients[0].name ?? recipients[0].phone)
            : `${total.toLocaleString()}명`;
          const d = new Date(row.scheduled_at || row.sent_at);
          const mo = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          const h = String(d.getHours()).padStart(2, "0");
          const mi = String(d.getMinutes()).padStart(2, "0");
          return {
            id: row.id,
            title: row.subject || row.message.slice(0, 20) + (row.message.length > 20 ? "..." : ""),
            target,
            time: `${mo}.${day} ${h}:${mi}`,
            type: row.type,
          };
        });
        setScheduledItems(mapped);
        setScheduledCount(mapped.length);
      }
    } catch (err) {
      console.error("[Dashboard] 예약 발송 조회 예외:", err);
    } finally {
      setScheduledLoading(false);
      setStatsLoading(false);
    }
  }, [user]);

  const fetchNotices = useCallback(async () => {
    try {
      const { data: json, error } = await supabase.functions.invoke("get-public-notices", {});
      if (error) throw error;
      setNotices((json?.notices ?? []).slice(0, 5));
    } catch (err) {
      console.error("[Dashboard] notices fetch error:", err);
    } finally {
      setNoticesLoading(false);
    }
  }, []);

  // Poll for scheduled sends that are due (after fetchStats/showToast are defined)
  useScheduledSendPoller({
    onProcessed: (_count, _results) => {
      fetchStats();
      refreshBalanceAlert();
    },
    onRefreshNeeded: () => {
      fetchStats();
      refreshBalanceAlert();
    },
  });

  // 발송 완료 후 통계 + 잔액 경고를 함께 갱신
  const handleSendComplete = useCallback(() => {
    fetchStats();
    refreshBalanceAlert();
  }, [fetchStats, refreshBalanceAlert]);

  useEffect(() => {
    if (user && session) {
      // 24시간 지난 발송 내역 자동 정리 (백그라운드)
      supabase.rpc("cleanup_old_sends", { hours_to_keep: 24 }).then(({ data, error }) => {
        if (!error && data?.deleted > 0) {
          console.log(`[Dashboard] ${data.deleted}건의 오래된 발송 내역 정리 완료`);
        }
      });
      fetchStats();
      fetchNotices();
    }
  }, [user, session, fetchStats, fetchNotices]);

  // 통계/예약/공지 데이터를 캐시에 저장해 탭 복귀 시 즉시 복원
  useEffect(() => {
    if (statsLoading) return;
    const snapshot: DashboardStatsCache = {
      credit,
      monthlySent,
      successRate,
      scheduledCount,
      weeklyStats,
      monthlyStats,
      scheduledItems,
    };
    statsCacheRef.current = snapshot;
    writeStatsCache(snapshot);
  }, [credit, monthlySent, successRate, scheduledCount, weeklyStats, monthlyStats, scheduledItems, statsLoading]);

  useEffect(() => {
    if (noticesLoading) return;
    noticesCacheRef.current = notices;
    writeNoticesCache(notices);
  }, [notices, noticesLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  const getUserName = () => {
    const name = user.user_metadata?.full_name as string | undefined;
    return name ?? user.email?.split("@")[0] ?? "사용자";
  };

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex flex-col md:flex-row">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      {/* Mobile Slide Menu */}
      <MobileSlideMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Toast notifications */}
      <div className="fixed top-20 right-6 z-[100] space-y-3 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-md transition-all animate-in slide-in-from-right duration-300 ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${
              toast.type === "success" ? "bg-emerald-500/20" : "bg-red-500/20"
            }`}>
              <i className={`${toast.type === "success" ? "ri-check-line" : "ri-close-line"} text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {toast.type === "success" ? "충전 완료" : "충전 거절"}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{toast.message}</p>
            </div>
            <button
              onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        ))}
      </div>

      {/* Main content */}
      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen md:pb-0 pb-20`}>
        {/* Top bar */}
        <header className="h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              className="md:hidden w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer shrink-0"
              onClick={() => setMobileMenuOpen(true)}
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-base">대시보드</h1>
              <p className="text-gray-500 text-xs">
                안녕하세요, <span className="text-violet-400">{getUserName()}</span>님
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => setChargeModalOpen(true)}
              className="flex items-center gap-1 md:gap-1.5 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-3 md:px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all hover:scale-105"
            >
              <i className="ri-add-circle-line text-sm"></i>
              <span className="hidden sm:inline">충전하기</span>
            </button>
            <UserNotificationBell
              userId={user.id}
              onToast={showToast}
              onCreditUpdate={setCredit}
            />
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* 잔액 부족 실패 경고 */}
          <BalanceFailureBanner state={balanceAlert} />

          {/* QuickSend - FIRST on mobile, side on desktop */}
          <div className="md:hidden">
            <QuickSend onSendComplete={handleSendComplete} credit={credit} perMessageRate={perMessageRate} />
          </div>

          {/* Stats */}
          <StatsCards
            credit={credit}
            monthlySent={monthlySent}
            successRate={successRate}
            scheduledCount={scheduledCount}
            loading={statsLoading}
            perMessageRate={perMessageRate}
          />

          {/* Middle row: Chart + Quick Send */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2">
              <SendChart weeklyData={weeklyStats} monthlyData={monthlyStats} loading={statsLoading} />
            </div>
            <div className="hidden md:block">
              <QuickSend onSendComplete={handleSendComplete} credit={credit} perMessageRate={perMessageRate} />
            </div>
          </div>

          {/* Recent history */}
          <RecentHistory />

          {/* Charge Modal */}
          <ChargeModal
            isOpen={chargeModalOpen}
            onClose={() => setChargeModalOpen(false)}
            onChargeComplete={(bal) => { setCredit(bal); }}
          />

          {/* Bottom row: Scheduled + Notice + Deletion Logs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {/* Scheduled sends */}
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-5">
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">예약 발송 대기</h3>
                  <p className="text-gray-500 text-xs mt-0.5">발송 예정 목록</p>
                </div>
                <span className="bg-violet-500/20 text-violet-400 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                  {scheduledCount}건
                </span>
              </div>
              <div className="space-y-2 md:space-y-3">
                {scheduledLoading ? (
                  <div className="py-8 text-center">
                    <div className="w-6 h-6 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-500 text-xs">불러오는 중...</p>
                  </div>
                ) : scheduledItems.length === 0 ? (
                  <div className="py-8 text-center">
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-white/[0.04]">
                      <i className="ri-calendar-check-line text-gray-600 text-xl"></i>
                    </div>
                    <p className="text-gray-500 text-sm">예약된 발송이 없습니다</p>
                  </div>
                ) : (
                  scheduledItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-2 md:gap-3 p-2.5 md:p-3 bg-white/[0.03] rounded-xl border border-white/[0.06] hover:border-white/[0.1] transition-colors">
                      <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-violet-500/20 shrink-0">
                        <i className="ri-calendar-schedule-line text-violet-400 text-sm md:text-base"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs md:text-sm font-medium truncate">{item.title}</p>
                        <p className="text-gray-500 text-xs truncate">{item.target}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-gray-300 text-xs font-medium whitespace-nowrap">{item.time}</p>
                        <span className="text-xs text-violet-400">{item.type}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Notice */}
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-5">
                <div>
                  <h3 className="text-white font-semibold text-sm md:text-base">공지사항</h3>
                  <p className="text-gray-500 text-xs mt-0.5">최신 업데이트 및 안내</p>
                </div>
                <span className="text-xs text-gray-500">{notices.length}건</span>
              </div>
              <div className="space-y-2 md:space-y-3">
                {noticesLoading ? (
                  <div className="py-6 text-center">
                    <div className="w-5 h-5 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-gray-500 text-xs">불러오는 중...</p>
                  </div>
                ) : notices.length === 0 ? (
                  <div className="py-6 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 rounded-full bg-white/[0.04]">
                      <i className="ri-megaphone-line text-gray-600 text-lg"></i>
                    </div>
                    <p className="text-gray-500 text-sm">등록된 공지사항이 없습니다</p>
                  </div>
                ) : (
                  notices.map((item) => {
                    const badgeColor =
                      item.badge === "공지"
                        ? "bg-violet-500/20 text-violet-400"
                        : item.badge === "업데이트"
                        ? "bg-sky-500/20 text-sky-400"
                        : item.badge === "이벤트"
                        ? "bg-amber-500/20 text-amber-400"
                        : item.badge === "점검"
                        ? "bg-red-500/20 text-red-400"
                        : "bg-white/[0.06] text-gray-400";
                    const isNew = new Date().getTime() - new Date(item.created_at).getTime() <= 7 * 24 * 60 * 60 * 1000;
                    return (
                      <div key={item.id} className="flex items-start gap-2 md:gap-3 p-2.5 md:p-3 hover:bg-white/[0.03] rounded-xl transition-colors cursor-pointer group">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-md whitespace-nowrap shrink-0 mt-0.5 ${badgeColor}`}>
                          {item.badge}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-300 text-xs md:text-sm group-hover:text-white transition-colors truncate flex items-center gap-1.5">
                            {item.title}
                            {isNew && <span className="inline-block w-1.5 h-1.5 bg-pink-500 rounded-full shrink-0"></span>}
                          </p>
                          <p className="text-gray-600 text-xs mt-0.5">
                            {new Date(item.created_at).toLocaleDateString("ko-KR", { year: "numeric", month: "short", day: "numeric" })}
                          </p>
                        </div>
                        {item.is_pinned && (
                          <i className="ri-pushpin-2-fill text-rose-400/60 text-xs shrink-0 mt-1"></i>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent Deletion Logs */}
            <RecentDeletionLogs />
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav onOpenMenu={() => setMobileMenuOpen(true)} />
    </div>
  );
}